<script lang="ts">
import ConfirmModal from '@/components/ConfirmModal.vue';
import NumberInput from '@/components/v2/NumberInput.vue';
import TextField from '@/components/v2/TextField.vue';
import { Trash2 } from '@lucide/vue';

export default {
  name: 'AccountExposureDeleteModal',
  components: { ConfirmModal, NumberInput, TextField, Trash2 },
};
</script>

<script setup lang="ts">
import { ExposureValue, ExposureProperty } from '@/types/AccountExposure';
import { filters } from '@/utilities/Filters';

const props = withDefaults(
  defineProps<{
    exposure: ExposureValue | ExposureProperty;
    delete: (exposure: ExposureValue | ExposureProperty) => Promise<unknown>;
    isPropertyExposure?: boolean;
    /** When true, modal opens on mount and trigger button is hidden (controlled mode). */
    show?: boolean;
    /** Optional extra class(es) for the trigger button (e.g. opacity + group-hover for row hover effect). */
    triggerClass?: string;
  }>(),
  { show: false }
);

const emit = defineEmits<{ closed: [] }>();

async function onConfirm(): Promise<boolean> {
  try {
    await props.delete(props.exposure);
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
}
</script>

<template>
  <ConfirmModal
    :action="onConfirm"
    confirmText="Delete"
    title="Delete Exposure Entry"
    size="md"
    :show="show"
    @closed="emit('closed')">
    <template #button="{ props: slotProps }">
      <button
        v-if="!show"
        type="button"
        :class="['btn btn-square btn-ghost btn-sm', props.triggerClass]"
        data-testid="delete-exposure-entry"
        @click="slotProps?.onClick">
        <Trash2 :size="16" />
      </button>
    </template>
    <div class="flex flex-col gap-4">
      <p class="m-0">Are you sure you want to delete this entry?</p>
      <div class="sm:flex-row sm:flex-wrap flex flex-col gap-4">
        <NumberInput
          v-if="!isPropertyExposure"
          :model-value="'value' in exposure ? exposure.value : ''"
          :label="'name' in exposure && exposure.name ? String(exposure.name) : undefined"
          prefix="$"
          :disabled="true" />
        <NumberInput
          v-else
          :model-value="'address1' in exposure ? exposure.address1 : ''"
          label="Address"
          :disabled="true" />
        <TextField
          :model-value="filters.date(exposure.effectiveDate)"
          label="Effective Date"
          disabled />
      </div>
    </div>
  </ConfirmModal>
</template>
